import React, { useState, useEffect } from 'react';
import DatePicker from 'react-date-picker';
import { toast } from 'react-toastify';
import Select from 'react-select';

import styledComponent from '../utils/styledComponent';
import {
  GetOpsLocationsFulfillmentCenters,
  CreateNextmv,
  GetTrafficConfiguration,
  UpdateTrafficConfiguration,
} from '../libs/GraphQL';
import ConfirmationModal from '../components/ConfirmationModal';
import TrafficConfigTable from '../components/TrafficConfigTable';
import yyyymmdd from '../libs/date_convert';

const toastDefaultOptions = {
  className: 'prettyToast',
  position: 'top-center',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'light',
};

// Day of week abbreviations
const DAY_ABBREV = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// Simple ID generator for internal vehicle config tracking
const generateConfigId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Hardcoded FC code mapping for vehicle ID generation
// Format: "ST_City" (e.g., "IL_Chicago", "TX_Dallas")
const FC_CODE_MAP = {
  IL_Chicago: { stateCode: 'il', cityCode: 'chi' },
  'CA_Los Angeles': { stateCode: 'ca', cityCode: 'la' },
  WI_Milwaukee: { stateCode: 'wi', cityCode: 'mke' },
  IN_Indianapolis: { stateCode: 'in', cityCode: 'ind' },
  MN_Minneapolis: { stateCode: 'mn', cityCode: 'msp' },
  MI_Detroit: { stateCode: 'mi', cityCode: 'det' },
  TX_Dallas: { stateCode: 'tx', cityCode: 'dal' },
  TX_Houston: { stateCode: 'tx', cityCode: 'hou' },
  TX_Austin: { stateCode: 'tx', cityCode: 'aus' },
  CO_Denver: { stateCode: 'co', cityCode: 'den' },
  AZ_Phoenix: { stateCode: 'az', cityCode: 'phx' },
  GA_Atlanta: { stateCode: 'ga', cityCode: 'atl' },
  TN_Nashville: { stateCode: 'tn', cityCode: 'nas' },
  'MO_St. Louis': { stateCode: 'mo', cityCode: 'stl' },
  'MO_Kansas City': { stateCode: 'mo', cityCode: 'kc' },
  OH_Cincinnati: { stateCode: 'oh', cityCode: 'cin' },
  OH_Columbus: { stateCode: 'oh', cityCode: 'col' },
  OH_Cleveland: { stateCode: 'oh', cityCode: 'cle' },
};

// Generate time options in 15-minute intervals
const generateTimeOptionsArray = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h12 = hour % 12 || 12;
      const m = minute.toString().padStart(2, '0');
      const ampm = hour < 12 ? 'AM' : 'PM';
      const display = `${h12}:${m} ${ampm}`;
      const value = `${hour.toString().padStart(2, '0')}:${m}`;
      options.push({ display, value });
    }
  }
  return options;
};

const StyledDiv = styledComponent('div', {
  '& .page-header': {
    fontSize: '28px',
    fontWeight: 500,
    color: '#333',
    marginBottom: '30px',
    paddingBottom: '15px',
    borderBottom: '1px solid #e0e0e0',
  },
  // ...existing code...
  '& .container': {
    maxWidth: '1400px',
    margin: '0 auto',
    background: 'white',
    padding: '30px',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  // ...existing code...
  '& .filter-row': {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  // ...existing code...
  '& .input-wrapper': {
    textAlign: 'center',
  },
  '& .input-label': {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '8px',
  },
  '& .required-indicator': {
    color: '#d32f2f',
    marginLeft: '2px',
  },
  '& .date-picker': {
    padding: '8px 12px',
    borderRadius: 0,
    fontSize: '14px',
    width: '170px',
  },
  '& .fc-tags': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
    padding: '5px',
    border: '1px solid #ccc',
    minHeight: '40px',
    width: '300px',
    background: 'white',
  },
  '& .fc-tag': {
    background: '#e0e0e0',
    padding: '4px 8px',
    borderRadius: '2px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  '& .fc-tag .remove': {
    cursor: 'pointer',
    color: '#666',
    fontWeight: 'bold',
    '&:hover': {
      color: '#333',
    },
  },
  '& .fc-vehicle-section': {
    marginTop: '30px',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  '& .fc-header': {
    background: '#5a5a5a',
    color: 'white',
    padding: '12px 20px',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  '& .fc-title-group': {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  '& .fc-timezone-badge': {
    background: '#4CAF50',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
  },
  '& .add-vehicle-dropdown': {
    position: 'relative',
    display: 'inline-block',
  },
  '& .add-vehicle-btn': {
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    padding: '6px 14px',
    fontSize: '13px',
    cursor: 'pointer',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    '&:hover': {
      background: '#45a049',
    },
  },
  '& .dropdown-content': {
    display: 'none',
    position: 'absolute',
    right: 0,
    background: 'white',
    minWidth: '160px',
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.2)',
    zIndex: 10,
    borderRadius: '4px',
    overflow: 'hidden',
    top: '100%',
    marginTop: '5px',
  },
  '& .dropdown-overlay': {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9,
  },
  '& .dropdown-content.show': {
    display: 'block',
  },
  '& .dropdown-item': {
    color: '#333',
    padding: '12px 16px',
    textDecoration: 'none',
    display: 'block',
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#f1f1f1',
    },
  },
  '& .shift-time': {
    fontSize: '11px',
    color: '#666',
    marginTop: '2px',
  },
  '& .fc-body': {
    padding: '15px',
    overflow: 'hidden',
  },
  '& .vehicle-type-card': {
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginBottom: '15px',
    background: '#fafafa',
  },
  '& .vehicle-type-header': {
    background: '#e8e8e8',
    padding: '10px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    borderBottom: '1px solid #ddd',
  },
  '& .vehicle-type-title': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  '& .vehicle-type-badge': {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: 500,
  },
  '& .vehicle-type-badge.am': {
    background: '#fff3e0',
    color: '#e65100',
  },
  '& .vehicle-type-badge.pm': {
    background: '#e3f2fd',
    color: '#1565c0',
  },
  '& .remove-vehicle-btn': {
    background: '#d32f2f',
    color: 'white',
    border: 'none',
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    borderRadius: '3px',
    '&:hover': {
      background: '#b71c1c',
    },
    '&:disabled': {
      background: '#999',
      cursor: 'not-allowed',
    },
  },
  '& .vehicle-fields': {
    padding: '15px',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
  },
  '& .field-group': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .field-label': {
    fontSize: '12px',
    fontWeight: 600,
    color: '#555',
    marginBottom: '4px',
  },
  '& .field-input, & .field-select': {
    padding: '6px 8px',
    border: '1px solid #ccc',
    borderRadius: 0,
    fontSize: '13px',
    '&:focus': {
      outline: 'none',
      borderColor: '#5b7cda',
    },
  },
  '& .field-input.readonly': {
    background: '#f0f0f0',
    color: '#666',
  },
  '& .field-hint': {
    color: '#888',
    fontSize: '10px',
    marginTop: '2px',
  },
  '& .vehicle-count-section': {
    padding: '15px',
    borderTop: '1px solid #ddd',
    background: 'white',
  },
  '& .vehicle-count-label': {
    fontSize: '13px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '10px',
  },
  '& .vehicle-count-row': {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  '& .count-input': {
    width: '80px',
    padding: '6px',
    border: '1px solid #ccc',
    borderRadius: 0,
    fontSize: '13px',
    textAlign: 'center',
  },
  '& .empty-state': {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    fontStyle: 'italic',
  },
  '& .button-container': {
    marginTop: '30px',
    textAlign: 'center',
  },
  '& .generate-run-btn': {
    background: '#5b7cda',
    color: 'white',
    border: 'none',
    padding: '14px 60px',
    fontSize: '18px',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: 0,
    minWidth: '250px',
    '&:hover': {
      background: '#4a6bc9',
    },
    '&:disabled': {
      background: '#ccc',
      cursor: 'not-allowed',
    },
  },
  '@media (max-width: 1200px)': {
    '& .vehicle-fields': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
  '@media (max-width: 900px)': {
    '& .vehicle-fields': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  '@media (max-width: 600px)': {
    '& .vehicle-fields': {
      gridTemplateColumns: '1fr',
    },
    '& .filter-row': {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  '& .mv-table': {
    textAlign: 'center',
  },
  '& .mv-table th, & .mv-table td': {
    textAlign: 'center',
  },
  // Traffic configuration styles
  '& .traffic-config-section': {
    marginTop: '15px',
    borderTop: '1px solid #ddd',
    paddingTop: '15px',
  },
  '& .traffic-config-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '10px 15px',
    background: '#f8f8f8',
    borderRadius: '4px',
    marginBottom: '10px',
  },
  '& .traffic-config-header:hover': {
    background: '#f0f0f0',
  },
  '& .traffic-config-title': {
    fontWeight: 600,
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  '& .traffic-config-badge': {
    background: '#ff9800',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
  },
  '& .traffic-config-body': {
    padding: '10px',
  },
  '& .traffic-loading': {
    textAlign: 'center',
    padding: '20px',
    color: '#666',
  },
  '& .collapse-icon': {
    transition: 'transform 0.2s',
  },
  '& .collapse-icon.expanded': {
    transform: 'rotate(180deg)',
  },
  '& .reset-defaults-btn': {
    background: '#fff',
    color: '#f57c00',
    border: '1px solid #f57c00',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    borderRadius: '3px',
    fontWeight: 500,
    '&:hover': {
      background: '#fff3e0',
      borderColor: '#ef6c00',
      color: '#ef6c00',
    },
    '&:disabled': {
      background: '#fafafa',
      color: '#ccc',
      borderColor: '#ddd',
      cursor: 'not-allowed',
    },
  },
  '& .traffic-config-buttons': {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  '& .save-traffic-btn': {
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    borderRadius: '3px',
    '&:hover': {
      background: '#45a049',
    },
    '&:disabled': {
      background: '#a5d6a7',
      cursor: 'not-allowed',
    },
  },
});

function ViewNextmv() {
  const today = new Date();
  const [dropOffDate, setDropOffDate] = useState(today);
  const [fulfillmentCentersList, setFulfillmentCentersList] = useState([]);
  const [fcTimezoneMap, setFcTimezoneMap] = useState({});
  const [selectedFCs, setSelectedFCs] = useState([]);
  const [vehicleConfigs, setVehicleConfigs] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInBlockedTime, setIsInBlockedTime] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Cooldown state (10 minutes after successful submission)
  const [cooldownEndTime, setCooldownEndTime] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  // Traffic configuration state
  const [trafficConfigs, setTrafficConfigs] = useState({});
  const [originalTrafficConfigs, setOriginalTrafficConfigs] = useState({});
  const [trafficConfigsExpanded, setTrafficConfigsExpanded] = useState({});
  const [trafficConfigsLoading, setTrafficConfigsLoading] = useState({});

  const timeOptions = generateTimeOptionsArray();

  const checkBlockedTimeWindow = () => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const currentHour = Number.parseInt(
      parts.find((p) => p.type === 'hour').value,
      10,
    );
    const currentMinute = Number.parseInt(
      parts.find((p) => p.type === 'minute').value,
      10,
    );
    return currentHour === 17 && currentMinute >= 30 && currentMinute <= 50;
  };

  // Format remaining cooldown time as mm:ss
  const formatCooldownTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleDropdownMenu = (fc, e) => {
    if (e) e.stopPropagation();
    setOpenDropdown(openDropdown === fc ? null : fc);
  };

  const handleDropOffDateChange = (value) => {
    setDropOffDate(value);
  };

  const handleFCChange = (selectedOptions) => {
    const fcValues = selectedOptions?.map((opt) => opt.value) || [];
    setSelectedFCs(fcValues);

    setVehicleConfigs((prevConfigs) => {
      const newConfigs = {};

      // Keep existing configs for still-selected FCs and add defaults for new ones
      fcValues.forEach((fc) => {
        if (prevConfigs[fc]) {
          newConfigs[fc] = prevConfigs[fc];
        } else {
          newConfigs[fc] = [
            {
              id: generateConfigId(),
              vehicleType: 'AM',
              numVehicles: 1,
              capacity: '70',
              shiftStartTime: '04:00',
              targetShiftEndTime: '14:30',
              shiftEndTime: '16:00',
              lat: '',
              long: '',
              skillSet: 'Daytime',
            },
          ];
        }
      });

      return newConfigs;
    });

    // Fetch traffic config for newly selected FCs
    fcValues.forEach((fc) => {
      if (!trafficConfigs[fc]) {
        fetchTrafficConfig(fc);
      }
    });

    setOpenDropdown(null);
  };

  const handleVehicleFieldChange = (fc, vehicleId, field, value) => {
    setVehicleConfigs((prevConfigs) => ({
      ...prevConfigs,
      [fc]: prevConfigs[fc].map((vehicle) =>
        vehicle.id === vehicleId ? { ...vehicle, [field]: value } : vehicle,
      ),
    }));
  };

  const addVehicleType = (fc, shiftType, e) => {
    if (e) e.stopPropagation();

    const shiftDefaults = {
      AM: {
        start: '04:00',
        targetEnd: '14:30',
        end: '16:00',
      },
      PM: {
        start: '14:00',
        targetEnd: '20:30',
        end: '22:00',
      },
    };

    const defaults = shiftDefaults[shiftType] || shiftDefaults.AM;

    const newVehicle = {
      id: generateConfigId(),
      vehicleType: shiftType,
      numVehicles: 1,
      capacity: '70',
      shiftStartTime: defaults.start,
      targetShiftEndTime: defaults.targetEnd,
      shiftEndTime: defaults.end,
      lat: '',
      long: '',
      skillSet: shiftType === 'AM' ? 'Daytime' : 'Overnight',
    };

    setVehicleConfigs((prevConfigs) => ({
      ...prevConfigs,
      [fc]: [...(prevConfigs[fc] || []), newVehicle],
    }));

    setOpenDropdown(null);
  };

  const removeVehicle = (fc, vehicleId) => {
    setVehicleConfigs((prevConfigs) => ({
      ...prevConfigs,
      [fc]: prevConfigs[fc].filter((v) => v.id !== vehicleId),
    }));
  };

  // Traffic configuration functions
  // Convert object with numeric keys to sorted array (API returns trafficMultiplier, we use magnifier)
  const convertTrafficConfigObject = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.values(obj)
      .map((item) => ({
        startTime: item.startTime,
        endTime: item.endTime,
        magnifier: item.trafficMultiplier,
      }))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const fetchTrafficConfig = async (fc) => {
    if (trafficConfigs[fc]) return; // Skip if already loaded

    const emptyConfig = { weekday: [], weekend: [] };

    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: true }));
    try {
      const result = await GetTrafficConfiguration(fc);
      // Parse trafficConfiguration - it may be a JSON string or already parsed
      let parsedConfig = result?.trafficConfiguration;
      if (typeof parsedConfig === 'string') {
        parsedConfig = JSON.parse(parsedConfig);
      }
      // Convert from object with numeric keys to array format
      const config = {
        weekday: convertTrafficConfigObject(parsedConfig?.weekday),
        weekend: convertTrafficConfigObject(parsedConfig?.weekend),
      };
      setOriginalTrafficConfigs((prev) => ({ ...prev, [fc]: config }));
      setTrafficConfigs((prev) => ({ ...prev, [fc]: config }));
      setTrafficConfigsExpanded((prev) => ({ ...prev, [fc]: true }));
    } catch (e) {
      console.error(`Failed to fetch traffic config for ${fc}:`, e);
      toast.error(
        `Failed to load traffic configuration for ${fc}`,
        toastDefaultOptions,
      );
      setTrafficConfigs((prev) => ({ ...prev, [fc]: emptyConfig }));
      setOriginalTrafficConfigs((prev) => ({ ...prev, [fc]: emptyConfig }));
    }
    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: false }));
  };

  const handleTrafficConfigChange = (fc, dayType, windows) => {
    setTrafficConfigs((prev) => ({
      ...prev,
      [fc]: {
        ...prev[fc],
        [dayType]: windows,
      },
    }));
  };

  const toggleTrafficConfigExpanded = (fc) => {
    setTrafficConfigsExpanded((prev) => ({ ...prev, [fc]: !prev[fc] }));
  };

  const hasTrafficConfigModifications = (fc) => {
    const original = originalTrafficConfigs[fc];
    const current = trafficConfigs[fc];
    if (!original || !current) return false;
    return JSON.stringify(original) !== JSON.stringify(current);
  };

  const resetTrafficConfigToDefaults = (fc) => {
    const original = originalTrafficConfigs[fc];
    if (original) {
      setTrafficConfigs((prev) => ({
        ...prev,
        // eslint-disable-next-line prefer-object-spread
        [fc]: JSON.parse(JSON.stringify(original)),
      }));
    }
  };

  const validateTrafficConfigForFC = (fc) => {
    const trafficConfig = trafficConfigs[fc] || { weekday: [], weekend: [] };
    const allWindows = [
      ...(trafficConfig.weekday || []).map((w) => ({
        ...w,
        dayType: 'weekday',
      })),
      ...(trafficConfig.weekend || []).map((w) => ({
        ...w,
        dayType: 'weekend',
      })),
    ];

    const hasError = allWindows.some((window) => {
      const magnifierValue = Number.parseFloat(window.magnifier);

      if (
        window.magnifier === '' ||
        window.magnifier === undefined ||
        window.magnifier === null ||
        Number.isNaN(magnifierValue)
      ) {
        toast.error(
          `${fc}: Magnifier for ${window.startTime} (${window.dayType}) must be a valid number`,
          toastDefaultOptions,
        );
        return true;
      }

      if (magnifierValue < 1) {
        toast.error(
          `${fc}: Magnifier for ${window.startTime} (${window.dayType}) cannot be less than 1`,
          toastDefaultOptions,
        );
        return true;
      }

      if (magnifierValue > 10) {
        toast.error(
          `${fc}: Magnifier for ${window.startTime} (${window.dayType}) cannot be greater than 10`,
          toastDefaultOptions,
        );
        return true;
      }

      const decimalPart = String(window.magnifier).split('.')[1];
      if (decimalPart && decimalPart.length > 2) {
        toast.error(
          `${fc}: Magnifier for ${window.startTime} (${window.dayType}) can have maximum 2 decimal places`,
          toastDefaultOptions,
        );
        return true;
      }

      return false;
    });

    return !hasError;
  };

  const handleSaveTrafficConfig = async (fc) => {
    if (!validateTrafficConfigForFC(fc)) {
      return;
    }

    const trafficConfig = trafficConfigs[fc] || { weekday: [], weekend: [] };
    const formattedTrafficConfig = [
      ...(trafficConfig.weekday || []).map((window) => ({
        fulfillmentCenter: fc,
        dayType: 'weekday',
        startTime: window.startTime?.substring(0, 5),
        trafficMultiplier: Number.parseFloat(window.magnifier),
      })),
      ...(trafficConfig.weekend || []).map((window) => ({
        fulfillmentCenter: fc,
        dayType: 'weekend',
        startTime: window.startTime?.substring(0, 5),
        trafficMultiplier: Number.parseFloat(window.magnifier),
      })),
    ];

    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: true }));

    try {
      await UpdateTrafficConfiguration(formattedTrafficConfig);

      // Update original configs to reflect the saved state
      setOriginalTrafficConfigs((prev) => ({
        ...prev,
        // eslint-disable-next-line prefer-object-spread
        [fc]: JSON.parse(JSON.stringify(trafficConfigs[fc])),
      }));

      toast.success(
        `Traffic configuration for ${fc} saved successfully!`,
        toastDefaultOptions,
      );
    } catch (e) {
      console.error(`Failed to save traffic config for ${fc}:`, e);
      toast.error(
        `Failed to save traffic configuration for ${fc}`,
        toastDefaultOptions,
      );
    }

    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: false }));
  };

  const getFulfillmentCenters = async () => {
    setIsLoading(true);

    try {
      // Fetch all locations using pagination
      const allLocations = [];
      let nextToken = null;

      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await GetOpsLocationsFulfillmentCenters(nextToken);
        const locations = response?.data?.opsLocations?.locations || [];
        allLocations.push(...locations);
        nextToken = response?.data?.opsLocations?.nextToken;
      } while (nextToken);

      // Extract unique FCs and their timezones
      const fcMap = {};
      allLocations.forEach((location) => {
        if (
          location.fulfillmentCenter &&
          location.fulfillmentCenter !== 'Does Not Exist'
        ) {
          fcMap[location.fulfillmentCenter] = location.timeZone || '';
        }
      });

      setFcTimezoneMap(fcMap);

      const formattedCenters = Object.keys(fcMap)
        .sort()
        .map((fc) => ({ value: fc, label: fc }));

      setFulfillmentCentersList(formattedCenters);
    } catch (e) {
      console.error(e);
      toast.error('Failed fetching Fulfillment Centers', toastDefaultOptions);
    }

    setIsLoading(false);
  };

  const validateAllFields = () => {
    const hasError = selectedFCs.some((fc) => {
      if (!vehicleConfigs[fc] || vehicleConfigs[fc].length === 0) {
        toast.error(
          `Please add at least one vehicle for ${fc}`,
          toastDefaultOptions,
        );
        return true;
      }

      const vehicleError = vehicleConfigs[fc].some((vehicle) => {
        if (
          !vehicle.vehicleType ||
          !vehicle.capacity ||
          !vehicle.shiftStartTime ||
          !vehicle.targetShiftEndTime ||
          !vehicle.shiftEndTime ||
          !vehicle.lat ||
          !vehicle.long
        ) {
          toast.error(
            `Please fill all required fields for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        if (Number.isNaN(parseFloat(vehicle.capacity))) {
          toast.error(
            `Capacity must be a number for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        if (
          Number.isNaN(parseFloat(vehicle.lat)) ||
          Number.isNaN(parseFloat(vehicle.long))
        ) {
          toast.error(
            `Latitude and Longitude must be numbers for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        const lat = Number.isNaN(parseFloat(vehicle.lat))
          ? Number.NaN
          : Number.parseFloat(vehicle.lat);
        const long = Number.isNaN(parseFloat(vehicle.long))
          ? Number.NaN
          : Number.parseFloat(vehicle.long);

        if (lat < -90 || lat > 90) {
          toast.error(
            `Latitude must be between -90 and 90 for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        if (long < -180 || long > 180) {
          toast.error(
            `Longitude must be between -180 and 180 for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        return false;
      });

      if (vehicleError) return true;

      // Traffic configuration validation
      const trafficConfig = trafficConfigs[fc] || { weekday: [], weekend: [] };
      const allWindows = [
        ...(trafficConfig.weekday || []).map((w) => ({
          ...w,
          dayType: 'weekday',
        })),
        ...(trafficConfig.weekend || []).map((w) => ({
          ...w,
          dayType: 'weekend',
        })),
      ];
      const trafficError = allWindows.some((window, index) => {
        const magnifierValue = Number.parseFloat(window.magnifier);

        // Check if magnifier is a valid number
        if (
          window.magnifier === '' ||
          window.magnifier === undefined ||
          window.magnifier === null ||
          Number.isNaN(magnifierValue)
        ) {
          toast.error(
            `Traffic window ${index + 1} (${
              window.dayType
            }) for ${fc}: Magnifier must be a valid number`,
            toastDefaultOptions,
          );
          return true;
        }

        // Check if magnifier is >= 1
        if (magnifierValue < 1) {
          toast.error(
            `Traffic window ${index + 1} (${
              window.dayType
            }) for ${fc}: Magnifier cannot be less than 1`,
            toastDefaultOptions,
          );
          return true;
        }

        // Check if magnifier is <= 10
        if (magnifierValue > 10) {
          toast.error(
            `Traffic window ${index + 1} (${
              window.dayType
            }) for ${fc}: Magnifier cannot be greater than 10`,
            toastDefaultOptions,
          );
          return true;
        }

        // Check for max 2 decimal places
        const decimalPart = String(window.magnifier).split('.')[1];
        if (decimalPart && decimalPart.length > 2) {
          toast.error(
            `Traffic window ${index + 1} (${
              window.dayType
            }) for ${fc}: Magnifier can have maximum 2 decimal places`,
            toastDefaultOptions,
          );
          return true;
        }

        return false;
      });

      return trafficError;
    });

    return !hasError;
  };

  // Generate vehicle ID in format: {stateCode}_{cityCode}_{dayOfWeek}_{amOrPm}_{sequenceNumber}
  // Example: ca_la_tue_pm_1, il_chi_tue_am_2
  const generateVehicleId = (
    fc,
    vehicleType,
    sequenceNumber,
    dropOffDateValue,
  ) => {
    // Try direct lookup, then try without state prefix (e.g., "IL_Chicago" -> "Chicago")
    const normalizedFc = fc.includes('_')
      ? fc.split('_').slice(1).join('_')
      : fc;
    const fcCodes = FC_CODE_MAP[fc] || FC_CODE_MAP[normalizedFc];

    // Fallback to lowercase FC name if not in mapping
    const stateCode = fcCodes?.stateCode || 'xx';
    const cityCode =
      fcCodes?.cityCode || normalizedFc.toLowerCase().replaceAll(/\s+/g, '');

    // Get day of week from drop-off date
    const dayOfWeek = DAY_ABBREV[dropOffDateValue.getDay()];

    // AM or PM in lowercase
    const amOrPm = vehicleType.toLowerCase();

    return `${stateCode}_${cityCode}_${dayOfWeek}_${amOrPm}_${sequenceNumber}`;
  };

  const prepareSubmissionData = () => {
    const formattedDropOffDate = yyyymmdd(dropOffDate, '-');
    const submissionData = [];

    // Track sequence numbers per {stateCode}_{cityCode}_{dayOfWeek}_{amOrPm} combination
    const sequenceCounters = {};

    // Helper to get and increment sequence number for a combination
    const getNextSequenceNumber = (fc, vehicleType) => {
      const normalizedFc = fc.includes('_')
        ? fc.split('_').slice(1).join('_')
        : fc;
      const fcCodes = FC_CODE_MAP[fc] || FC_CODE_MAP[normalizedFc];
      const stateCode = fcCodes?.stateCode || 'xx';
      const cityCode =
        fcCodes?.cityCode || normalizedFc.toLowerCase().replaceAll(/\s+/g, '');
      const dayOfWeek = DAY_ABBREV[dropOffDate.getDay()];
      const amOrPm = vehicleType.toLowerCase();

      const key = `${stateCode}_${cityCode}_${dayOfWeek}_${amOrPm}`;
      sequenceCounters[key] = (sequenceCounters[key] || 0) + 1;
      return sequenceCounters[key];
    };

    // Create a submission for each fulfillment center
    selectedFCs.forEach((fc) => {
      const vehicles = [];

      vehicleConfigs[fc].forEach((config) => {
        const numVehicles = Number.parseInt(config.numVehicles, 10) || 1;

        for (let i = 0; i < numVehicles; i += 1) {
          const sequenceNumber = getNextSequenceNumber(fc, config.vehicleType);
          const vehicleId = generateVehicleId(
            fc,
            config.vehicleType,
            sequenceNumber,
            dropOffDate,
          );
          vehicles.push({
            id: vehicleId,
            capacity: Number.parseInt(config.capacity, 10),
            shiftStartTime: `${formattedDropOffDate}T${config.shiftStartTime}:00Z`,
            targetShiftEndTime: `${formattedDropOffDate}T${config.targetShiftEndTime}:00Z`,
            shiftEndTime: `${formattedDropOffDate}T${config.shiftEndTime}:00Z`,
            lat: Number.parseFloat(config.lat),
            long: Number.parseFloat(config.long),
            skillSet: config.vehicleType === 'AM' ? 'Daytime' : 'Overnight',
          });
        }
      });

      // Format traffic configuration for submission (keep endTime for display)
      const trafficConfig = trafficConfigs[fc] || { weekday: [], weekend: [] };
      const formattedTrafficConfig = [
        ...(trafficConfig.weekday || []).map((window) => ({
          fulfillmentCenter: fc,
          dayType: 'weekday',
          startTime: window.startTime?.substring(0, 5),
          endTime: window.endTime?.substring(0, 5),
          trafficMultiplier: Number.parseFloat(window.magnifier),
        })),
        ...(trafficConfig.weekend || []).map((window) => ({
          fulfillmentCenter: fc,
          dayType: 'weekend',
          startTime: window.startTime?.substring(0, 5),
          endTime: window.endTime?.substring(0, 5),
          trafficMultiplier: Number.parseFloat(window.magnifier),
        })),
      ];

      submissionData.push({
        dropOffDate: formattedDropOffDate,
        fulfillmentCenter: fc,
        vehicles,
        trafficConfiguration: formattedTrafficConfig,
      });
    });

    return {
      dropOffDate: formattedDropOffDate,
      fulfillmentCenters: selectedFCs,
      vehicles: submissionData,
    };
  };

  const handleGenerateRun = () => {
    if (!selectedFCs.length) {
      toast.error(
        'Please select at least one fulfillment center',
        toastDefaultOptions,
      );
      return;
    }

    if (!validateAllFields()) {
      return;
    }

    const data = prepareSubmissionData();
    setConfirmationData(data);
    setShowConfirmModal(true);
  };

  const handleSubmit = async () => {
    if (!confirmationData) return;
    setIsSubmitting(true);
    try {
      // Submit each FC's run with trafficConfiguration (strip endTime for API)
      const submissions = confirmationData.vehicles.map((vehicleRun) => {
        const trafficConfigForApi = (vehicleRun.trafficConfiguration || []).map(
          ({ endTime, ...rest }) => rest,
        );
        return CreateNextmv({
          ...vehicleRun,
          trafficConfiguration: trafficConfigForApi,
        });
      });
      const results = await Promise.all(submissions);

      // Log results and check for errors
      const failedRuns = results.filter((result) => {
        const statusCode = result.data?.createNextmvRun?.statusCode;
        return statusCode !== 200 && statusCode !== '200';
      });

      if (failedRuns.length > 0) {
        const errorMessages = failedRuns
          .map((r) => r.data?.createNextmvRun?.message || 'Unknown error')
          .join('; ');
        throw new Error(`Some runs failed: ${errorMessages}`);
      }

      toast.success(
        `${results.length} vehicle configuration(s) submitted successfully!`,
        toastDefaultOptions,
      );

      // Set 10-minute cooldown
      const cooldownDuration = 10 * 60 * 1000; // 10 minutes in milliseconds
      setCooldownEndTime(Date.now() + cooldownDuration);

      // Reset form
      setDropOffDate(today);
      setSelectedFCs([]);
      setVehicleConfigs({});
      setTrafficConfigs({});
      setOriginalTrafficConfigs({});
      setTrafficConfigsExpanded({});
      setShowConfirmModal(false);
      setConfirmationData(null);
    } catch (e) {
      console.error(e);
      toast.error(`Submission failed. ${e.message || ''}`, toastDefaultOptions);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    getFulfillmentCenters();
    setIsInBlockedTime(checkBlockedTimeWindow());

    const interval = setInterval(() => {
      setIsInBlockedTime(checkBlockedTimeWindow());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Cooldown timer effect
  useEffect(() => {
    if (!cooldownEndTime) {
      setCooldownRemaining(0);
      return undefined;
    }

    const updateCooldown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((cooldownEndTime - now) / 1000));
      setCooldownRemaining(remaining);

      if (remaining === 0) {
        setCooldownEndTime(null);
      }
    };

    // Update immediately
    updateCooldown();

    // Update every second
    const cooldownInterval = setInterval(updateCooldown, 1000);

    return () => clearInterval(cooldownInterval);
  }, [cooldownEndTime]);

  const isInCooldown = cooldownRemaining > 0;
  const isGenerateRunDisabled =
    isLoading || selectedFCs.length === 0 || isInBlockedTime || isInCooldown;

  const getGenerateButtonText = () => {
    if (isSubmitting) return 'Generating...';
    if (isInCooldown)
      return `Generate Run (Cooldown: ${formatCooldownTime(
        cooldownRemaining,
      )})`;
    return 'Generate Run';
  };

  return (
    <StyledDiv>
      <div className="container">
        <h1 className="page-header">Nextmv</h1>

        {/* Filter Row */}
        <div className="filter-row">
          <div className="input-wrapper">
            <div className="input-label">
              Drop Off Date<span className="required-indicator">*</span>
            </div>
            <DatePicker
              onChange={handleDropOffDateChange}
              value={dropOffDate}
              minDate={today}
              disabled={isSubmitting}
              format="MM/dd/y"
              className="date-picker"
            />
          </div>

          <div className="input-wrapper">
            <div className="input-label">
              Fulfillment Centers<span className="required-indicator">*</span>
            </div>
            <Select
              options={fulfillmentCentersList}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              onChange={handleFCChange}
              isLoading={isLoading}
              isDisabled={isLoading}
              value={fulfillmentCentersList.filter((opt) =>
                selectedFCs.includes(opt.value),
              )}
              maxMenuHeight={400}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: '0px',
                  border: '1px solid #ccc',
                  width: '300px',
                  minHeight: '40px',
                }),
              }}
            />
          </div>
        </div>

        {/* Overlay to close dropdown when clicking outside */}
        {openDropdown && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className="dropdown-overlay"
            onClick={() => setOpenDropdown(null)}
          />
        )}

        {/* FC Vehicle Sections */}
        <div className="fc-sections-container">
          {selectedFCs.length === 0 ? (
            <div className="empty-state">
              Please select at least one Fulfillment Center to configure vehicle
              information.
            </div>
          ) : (
            selectedFCs.map((fc) => (
              <div key={fc} className="fc-vehicle-section">
                <div className="fc-header">
                  <div className="fc-title-group">
                    <span>{fc} Vehicles</span>
                    {fcTimezoneMap[fc] && (
                      <span className="fc-timezone-badge">
                        {fcTimezoneMap[fc]}
                      </span>
                    )}
                  </div>
                  <div className="add-vehicle-dropdown">
                    <button
                      type="button"
                      className="add-vehicle-btn"
                      onClick={(e) => toggleDropdownMenu(fc, e)}
                    >
                      + Add Vehicle Type ▾
                    </button>
                    <div
                      className={`dropdown-content ${
                        openDropdown === fc ? 'show' : ''
                      }`}
                      onClick={(e) => e.stopPropagation()}
                      role="menu"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                        }
                      }}
                    >
                      <div
                        className="dropdown-item"
                        onClick={(e) => addVehicleType(fc, 'AM', e)}
                        role="menuitem"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            addVehicleType(fc, 'AM', e);
                          }
                        }}
                      >
                        <strong>AM Shift</strong>
                        <div className="shift-time">
                          04:00 - 16:00 (default)
                        </div>
                      </div>
                      <div
                        className="dropdown-item"
                        onClick={(e) => addVehicleType(fc, 'PM', e)}
                        role="menuitem"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            addVehicleType(fc, 'PM', e);
                          }
                        }}
                      >
                        <strong>PM Shift</strong>
                        <div className="shift-time">
                          14:00 - 22:00 (default)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fc-body">
                  {vehicleConfigs[fc] && vehicleConfigs[fc].length > 0 ? (
                    vehicleConfigs[fc].map((vehicle, index) => (
                      <div key={vehicle.id} className="vehicle-type-card">
                        <div className="vehicle-type-header">
                          <div className="vehicle-type-title">
                            <span>Vehicle Type #{index + 1}</span>
                            {vehicle.vehicleType && (
                              <span
                                className={`vehicle-type-badge ${vehicle.vehicleType.toLowerCase()}`}
                              >
                                {vehicle.vehicleType}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="remove-vehicle-btn"
                            onClick={() => removeVehicle(fc, vehicle.id)}
                            disabled={vehicleConfigs[fc].length === 1}
                          >
                            Remove
                          </button>
                        </div>

                        <div className="vehicle-fields">
                          <div className="field-group">
                            <label
                              htmlFor={`capacity-${vehicle.id}`}
                              className="field-label"
                            >
                              capacity
                              <span className="required-indicator">*</span>
                              <input
                                id={`capacity-${vehicle.id}`}
                                type="number"
                                className="field-input"
                                placeholder="e.g., 70"
                                min="1"
                                value={vehicle.capacity}
                                onChange={(e) =>
                                  handleVehicleFieldChange(
                                    fc,
                                    vehicle.id,
                                    'capacity',
                                    e.target.value,
                                  )
                                }
                              />
                            </label>
                            <span className="field-hint">
                              Default: 70 (editable)
                            </span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`shift-start-${vehicle.id}`}
                              className="field-label"
                            >
                              shift_start_time
                              <span className="required-indicator">*</span>
                              <select
                                id={`shift-start-${vehicle.id}`}
                                className="field-select"
                                value={vehicle.shiftStartTime}
                                onChange={(e) =>
                                  handleVehicleFieldChange(
                                    fc,
                                    vehicle.id,
                                    'shiftStartTime',
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select time</option>
                                {timeOptions.map((time) => (
                                  <option key={time.value} value={time.value}>
                                    {time.display}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <span className="field-hint">
                              → {yyyymmdd(dropOffDate, '-')}{' '}
                              {vehicle.shiftStartTime}:00
                            </span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`target-end-${vehicle.id}`}
                              className="field-label"
                            >
                              target_shift_end_time
                              <span className="required-indicator">*</span>
                              <select
                                id={`target-end-${vehicle.id}`}
                                className="field-select"
                                value={vehicle.targetShiftEndTime}
                                onChange={(e) =>
                                  handleVehicleFieldChange(
                                    fc,
                                    vehicle.id,
                                    'targetShiftEndTime',
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select time</option>
                                {timeOptions.map((time) => (
                                  <option key={time.value} value={time.value}>
                                    {time.display}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <span className="field-hint">
                              → {yyyymmdd(dropOffDate, '-')}{' '}
                              {vehicle.targetShiftEndTime}:00
                            </span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`shift-end-${vehicle.id}`}
                              className="field-label"
                            >
                              shift_end_time
                              <span className="required-indicator">*</span>
                              <select
                                id={`shift-end-${vehicle.id}`}
                                className="field-select"
                                value={vehicle.shiftEndTime}
                                onChange={(e) =>
                                  handleVehicleFieldChange(
                                    fc,
                                    vehicle.id,
                                    'shiftEndTime',
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select time</option>
                                {timeOptions.map((time) => (
                                  <option key={time.value} value={time.value}>
                                    {time.display}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <span className="field-hint">
                              → {yyyymmdd(dropOffDate, '-')}{' '}
                              {vehicle.shiftEndTime}:00
                            </span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`lat-${vehicle.id}`}
                              className="field-label"
                            >
                              lat<span className="required-indicator">*</span>
                              <input
                                id={`lat-${vehicle.id}`}
                                type="number"
                                step="0.000001"
                                className="field-input"
                                placeholder="e.g., 39.913467"
                                value={vehicle.lat}
                                onChange={(e) =>
                                  handleVehicleFieldChange(
                                    fc,
                                    vehicle.id,
                                    'lat',
                                    e.target.value,
                                  )
                                }
                              />
                            </label>
                            <span className="field-hint">Depot latitude</span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`long-${vehicle.id}`}
                              className="field-label"
                            >
                              long<span className="required-indicator">*</span>
                              <input
                                id={`long-${vehicle.id}`}
                                type="number"
                                step="0.000001"
                                className="field-input"
                                placeholder="e.g., -86.042816"
                                value={vehicle.long}
                                onChange={(e) =>
                                  handleVehicleFieldChange(
                                    fc,
                                    vehicle.id,
                                    'long',
                                    e.target.value,
                                  )
                                }
                              />
                            </label>
                            <span className="field-hint">Depot longitude</span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`skill-${vehicle.id}`}
                              className="field-label"
                            >
                              skill_set
                              <input
                                id={`skill-${vehicle.id}`}
                                type="text"
                                className="field-input readonly"
                                value={
                                  vehicle.vehicleType === 'AM'
                                    ? 'Daytime'
                                    : 'Overnight'
                                }
                                readOnly
                              />
                            </label>
                            <span className="field-hint">
                              Auto-populated based on shift type
                            </span>
                          </div>

                          <div className="field-group">
                            <label
                              htmlFor={`timezone-${vehicle.id}`}
                              className="field-label"
                            >
                              timezone
                              <input
                                id={`timezone-${vehicle.id}`}
                                type="text"
                                className="field-input readonly"
                                value={fcTimezoneMap[fc] || ''}
                                readOnly
                              />
                            </label>
                            <span className="field-hint">
                              Auto-populated from FC
                            </span>
                          </div>
                        </div>

                        <div className="vehicle-count-section">
                          <div className="vehicle-count-label">
                            Number of Vehicles
                            <span className="required-indicator">*</span>
                          </div>
                          <div className="vehicle-count-row">
                            <input
                              type="number"
                              className="count-input"
                              min="1"
                              value={vehicle.numVehicles}
                              onChange={(e) =>
                                handleVehicleFieldChange(
                                  fc,
                                  vehicle.id,
                                  'numVehicles',
                                  e.target.value,
                                )
                              }
                            />
                            <span className="field-hint">
                              vehicles of this type
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      No vehicle types defined. Click &quot;Add Vehicle
                      Type&quot; to add one.
                    </div>
                  )}

                  {/* Traffic Configuration Section */}
                  <div className="traffic-config-section">
                    <div
                      className="traffic-config-header"
                      onClick={() => toggleTrafficConfigExpanded(fc)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          toggleTrafficConfigExpanded(fc);
                        }
                      }}
                    >
                      <div className="traffic-config-title">
                        <span>Traffic Configuration</span>
                        {hasTrafficConfigModifications(fc) && (
                          <span className="traffic-config-badge">Modified</span>
                        )}
                      </div>
                      <span
                        className={`collapse-icon ${
                          trafficConfigsExpanded[fc] ? 'expanded' : ''
                        }`}
                      >
                        ▼
                      </span>
                    </div>

                    {trafficConfigsExpanded[fc] && (
                      <div className="traffic-config-body">
                        {trafficConfigsLoading[fc] ? (
                          <div className="traffic-loading">
                            Loading traffic configuration...
                          </div>
                        ) : (
                          <>
                            <div className="traffic-config-buttons">
                              <button
                                type="button"
                                className="save-traffic-btn"
                                onClick={() => handleSaveTrafficConfig(fc)}
                                disabled={
                                  isSubmitting ||
                                  !hasTrafficConfigModifications(fc)
                                }
                              >
                                Save Traffic Config
                              </button>
                              {hasTrafficConfigModifications(fc) && (
                                <button
                                  type="button"
                                  className="reset-defaults-btn"
                                  onClick={() =>
                                    resetTrafficConfigToDefaults(fc)
                                  }
                                  disabled={isSubmitting}
                                >
                                  Reset to Defaults
                                </button>
                              )}
                            </div>
                            <TrafficConfigTable
                              weekdayData={trafficConfigs[fc]?.weekday || []}
                              weekendData={trafficConfigs[fc]?.weekend || []}
                              originalWeekdayData={
                                originalTrafficConfigs[fc]?.weekday
                              }
                              originalWeekendData={
                                originalTrafficConfigs[fc]?.weekend
                              }
                              onWeekdayChange={(windows) =>
                                handleTrafficConfigChange(
                                  fc,
                                  'weekday',
                                  windows,
                                )
                              }
                              onWeekendChange={(windows) =>
                                handleTrafficConfigChange(
                                  fc,
                                  'weekend',
                                  windows,
                                )
                              }
                              disabled={isSubmitting}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Generate Run Button */}
        <div className="button-container">
          <button
            type="button"
            className="generate-run-btn"
            onClick={handleGenerateRun}
            disabled={isGenerateRunDisabled}
          >
            {getGenerateButtonText()}
          </button>
        </div>
      </div>

      <ConfirmationModal
        title="Confirm Vehicle Configuration Submission"
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => handleSubmit()}
        loading={isSubmitting}
      >
        {confirmationData && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <b>Drop-Off Date:</b> {confirmationData.dropOffDate}
              <br />
              <b>Fulfillment Centers:</b>{' '}
              {confirmationData.fulfillmentCenters.join(', ')}
              <br />
              <b>Total Submissions:</b> {confirmationData.vehicles.length}
            </div>

            <div style={{ overflowX: 'auto', marginTop: '12px' }}>
              {confirmationData.vehicles.map((run, runIndex) => (
                <div
                  key={run.fulfillmentCenter}
                  style={{ marginBottom: '20px' }}
                >
                  <h5 style={{ marginBottom: '10px', color: '#333' }}>
                    Run {runIndex + 1}: {run.fulfillmentCenter}
                  </h5>
                  <table
                    className="mv-table"
                    style={{
                      width: '100%',
                      fontSize: '12px',
                      textAlign: 'center',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Capacity</th>
                        <th>Shift Start</th>
                        <th>Target End</th>
                        <th>Shift End</th>
                        <th>Lat</th>
                        <th>Long</th>
                        <th>Skill Set</th>
                      </tr>
                    </thead>
                    <tbody>
                      {run.vehicles.map((v) => (
                        <tr key={v.id}>
                          <td>{v.id}</td>
                          <td>{v.capacity}</td>
                          <td>{v.shiftStartTime}</td>
                          <td>{v.targetShiftEndTime}</td>
                          <td>{v.shiftEndTime}</td>
                          <td>{v.lat}</td>
                          <td>{v.long}</td>
                          <td>{v.skillSet}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Traffic Configuration Summary */}
                  {run.trafficConfiguration?.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <h6 style={{ marginBottom: '8px', color: '#666' }}>
                        Traffic Configuration
                      </h6>
                      <table
                        style={{
                          width: '100%',
                          fontSize: '11px',
                          borderCollapse: 'collapse',
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '4px' }}>
                              Day Type
                            </th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>
                              Start Time
                            </th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>
                              End Time
                            </th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>
                              Multiplier
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {run.trafficConfiguration.map((w) => (
                            <tr
                              key={`${w.dayType}-${w.startTime}-${w.endTime}`}
                            >
                              <td style={{ padding: '4px' }}>{w.dayType}</td>
                              <td style={{ padding: '4px' }}>{w.startTime}</td>
                              <td style={{ padding: '4px' }}>{w.endTime}</td>
                              <td style={{ padding: '4px' }}>
                                {w.trafficMultiplier}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </ConfirmationModal>
    </StyledDiv>
  );
}

export default ViewNextmv;
